import * as Sentry from '@sentry/react'
import {
  address,
  appendTransactionMessageInstruction,
  compileTransaction,
  createTransactionMessage,
  getBase64EncodedWireTransaction,
  getTransactionDecoder,
  pipe,
  setTransactionMessageFeePayer,
  setTransactionMessageLifetimeUsingBlockhash,
} from '@solana/kit'
import {
  findAssociatedTokenPda,
  getTransferCheckedInstruction,
  getTransferInstruction,
} from '@solana-program/token'
import React, { useCallback, useContext, useEffect } from 'react'
import { ChainContext } from '@/contexts/SolanaChainContextProvider'
import { PhantomSolanaMobileContext } from '@/contexts/PhantomSolanaMobileContext'
import { RpcContext } from '@/contexts/SolanaRpcContext'
import {
  getSolanaTokenConfig,
  SOLANA_TOKEN_2022_PROGRAM,
} from '@/utils/currencies'
import { base64ToBytes } from '@/utils/phantomSolanaMobile'

type Props = {
  amount: number
  recipientAddress: string
  tokenSymbol: string
  senderAddress: string
  orderId: string
  onTransferComplete?: (txHash: string) => void
  onError?: (error: Error) => void
}

type ConnectedPhantomSolanaWalletButtonProps = {
  isProcessing: boolean
  props: Props
  setIsProcessing: (processing: boolean) => void
  showToast: (
    title: string,
    description: string,
    type: 'info' | 'warning' | 'success'
  ) => void
}

export const ConnectedPhantomSolanaWalletButton = React.memo(
  ({
    isProcessing,
    props,
    setIsProcessing,
    showToast,
  }: ConnectedPhantomSolanaWalletButtonProps) => {
    const {
      amount,
      onError,
      onTransferComplete,
      orderId,
      senderAddress,
      tokenSymbol,
    } = props
    const { chain: currentChain, solanaExplorerClusterName } =
      useContext(ChainContext)
    const { rpc } = useContext(RpcContext)
    const {
      session,
      signedTransaction,
      signTransaction,
      takeSignedTransaction,
    } = useContext(PhantomSolanaMobileContext)

    useEffect(() => {
      if (!signedTransaction || signedTransaction.orderId !== orderId) return
      const serializedTransaction = takeSignedTransaction(orderId)
      if (!serializedTransaction) return

      const submitSignedTransaction = async () => {
        setIsProcessing(true)
        try {
          const decodedTransaction = getTransactionDecoder().decode(
            serializedTransaction
          )
          const transactionSignature = await rpc
            .sendTransaction(
              getBase64EncodedWireTransaction(decodedTransaction),
              {
                encoding: 'base64',
                preflightCommitment: 'confirmed',
              }
            )
            .send()

          onTransferComplete?.(transactionSignature)
          showToast(
            'Transaction Submitted',
            'Your transaction was submitted successfully.',
            'success'
          )
        } catch (error) {
          const nextError =
            error instanceof Error
              ? error
              : new Error('Unable to submit the signed transaction')
          onError?.(nextError)
          showToast('Transaction Failed', nextError.message, 'warning')
          Sentry.captureException(nextError, {
            tags: { action: 'phantom_solana_mobile_submit' },
          })
        } finally {
          setIsProcessing(false)
        }
      }

      void submitSignedTransaction()
    }, [
      onError,
      onTransferComplete,
      orderId,
      rpc,
      setIsProcessing,
      showToast,
      signedTransaction,
      takeSignedTransaction,
    ])

    const handleClick = useCallback(async () => {
      if (!session) return

      try {
        if (session.walletPublicKey !== senderAddress) {
          throw new Error('Connect the Solana wallet used for this order')
        }
        if (solanaExplorerClusterName === 'testnet') {
          throw new Error('Solana testnet transactions are not supported')
        }

        setIsProcessing(true)
        const latestBlockhash = await rpc
          .getLatestBlockhash({ commitment: 'confirmed' })
          .send()
        const tokenConfig = getSolanaTokenConfig({
          cluster: solanaExplorerClusterName,
          tokenSymbol,
        })
        const mint = address(tokenConfig.mint)
        const tokenProgram = address(tokenConfig.tokenProgram)
        const sender = address(session.walletPublicKey)
        const destination = address(tokenConfig.destinationAta)
        const sourceTokenAccounts = await rpc
          .getTokenAccountsByOwner(sender, { mint }, { encoding: 'jsonParsed' })
          .send()

        if (!sourceTokenAccounts.value.length) {
          throw new Error(`Sender ${tokenSymbol} token account not found`)
        }

        const [sourceAta] = await findAssociatedTokenPda({
          mint,
          owner: sender,
          tokenProgram,
        })
        const amountInBaseUnits = BigInt(
          amount.toFixed(tokenConfig.decimals).replace('.', '')
        )
        const transferInstruction =
          tokenConfig.tokenProgram === SOLANA_TOKEN_2022_PROGRAM
            ? getTransferCheckedInstruction(
                {
                  amount: amountInBaseUnits,
                  authority: sender,
                  decimals: tokenConfig.decimals,
                  destination,
                  mint,
                  source: sourceAta,
                },
                { programAddress: tokenProgram }
              )
            : getTransferInstruction({
                amount: amountInBaseUnits,
                authority: sender,
                destination,
                source: sourceAta,
              })
        const transactionMessage = pipe(
          createTransactionMessage({ version: 0 }),
          (message) => setTransactionMessageFeePayer(sender, message),
          (message) =>
            setTransactionMessageLifetimeUsingBlockhash(
              latestBlockhash.value,
              message
            ),
          (message) =>
            appendTransactionMessageInstruction(transferInstruction, message)
        )
        const transaction = compileTransaction(transactionMessage)
        const serializedTransaction = base64ToBytes(
          getBase64EncodedWireTransaction(transaction)
        )

        showToast(
          'Opening Phantom',
          'Approve the transaction in Phantom to continue.',
          'info'
        )
        signTransaction({ orderId, serializedTransaction })
      } catch (error) {
        const nextError =
          error instanceof Error
            ? error
            : new Error('Unable to prepare the Solana transaction')
        setIsProcessing(false)
        onError?.(nextError)
        showToast('Transaction Failed', nextError.message, 'warning')
        Sentry.captureException(nextError, {
          tags: {
            action: 'phantom_solana_mobile_prepare',
            currentChain,
          },
        })
      }
    }, [
      currentChain,
      amount,
      onError,
      orderId,
      rpc,
      senderAddress,
      session,
      setIsProcessing,
      showToast,
      signTransaction,
      solanaExplorerClusterName,
      tokenSymbol,
    ])

    return (
      <button
        className="inline-flex w-full items-center justify-center border-none bg-black px-4 py-6 text-center text-white dark:bg-gray-200 dark:text-black"
        data-testid="btn-connected-phantom-solana-submit"
        disabled={isProcessing}
        onClick={handleClick}
        type="button"
      >
        <span>{`Send ${amount} ${tokenSymbol}`}</span>
      </button>
    )
  }
)

ConnectedPhantomSolanaWalletButton.displayName =
  'ConnectedPhantomSolanaWalletButton'
